// Daten-getriebene Landingpages. Jede Seite kennt ihren Rechner, ihre Texte und
// ihre internen Verlinkungen. SeoLandingPage rendert daraus generisch.
//
// calculator: "even" | "single" | "shelf" | null (null = noch ohne Rechner)
// Slugs entsprechen PRD §8. MVP-Rechner sind: even, single, shelf.

export const GROUPS = {
  bilder: "Bilder aufhängen",
  regal: "Regale & Objekte",
  moebel: "Möbel & Fenster",
  grundlagen: "Grundlagen",
};

export const PAGES = {
  "bilder-aufhaengen-abstand-berechnen": {
    slug: "bilder-aufhaengen-abstand-berechnen",
    group: GROUPS.bilder,
    calculator: "even",
    navLabel: "Bilder gleichmäßig aufhängen",
    h1: "Bilder gleichmäßig aufhängen: Abstand und Bohrpunkte berechnen",
    metaTitle: "Bilder gleichmäßig aufhängen: Abstand und Bohrpunkte berechnen",
    metaDescription:
      "Berechne Abstände, Bohrpunkte und Bohrhöhe, um mehrere Bilder gleichmäßig an der Wand aufzuhängen. Mit visueller Vorschau, kostenlos und ohne Anmeldung.",
    intro:
      "Trag Wandbreite, Anzahl und Bildbreite ein. Der Rechner verteilt die Bilder gleichmäßig und zeigt dir die Bohrpunkte von der linken Wandkante aus und die passende Bohrhöhe.",
    forWhat:
      "Für mehrere gleich breite Bilder, die in einer Reihe gleichmäßig an der Wand hängen sollen, etwa über einem Sofa, im Flur oder an der Treppe.",
    whichValues: [
      "Wandbreite, auf der die Bilder verteilt werden sollen",
      "Anzahl der Bilder",
      "Bildbreite (bei gleich breiten Bildern)",
      "optional Bildhöhe, Bildmitte ab Boden und Aufhängung von der Oberkante für die Bohrhöhe",
    ],
    howItWorks:
      "Der Rechner zieht die Gesamtbreite aller Bilder von der Wandbreite ab und verteilt den freien Platz gleichmäßig auf die Lücken inklusive der beiden Randlücken. Der Bohrpunkt sitzt jeweils in der Bildmitte.",
    example: {
      title: "Beispiel: 3 Bilder auf 200 cm Wand",
      lines: [
        "Wandbreite 200 cm, 3 Bilder, jedes 40 cm breit.",
        "Gesamtbreite der Bilder: 3 × 40 = 120 cm.",
        "Freier Platz: 200 − 120 = 80 cm, verteilt auf 4 Lücken = 20 cm je Lücke.",
        "Bohrpunkte von links: 40 cm, 100 cm, 160 cm (jeweils Bildmitte).",
      ],
    },
    mistakes: [
      "Bildhöhe mit Bohrhöhe verwechseln. Die Bohrhöhe hängt von der Aufhängung ab, nicht nur von der Bildmitte.",
      "Die Aufhängung nicht berücksichtigen. Öse oder Schnur sitzen meist einige Zentimeter unter der Oberkante.",
      "Die Randlücken vergessen. Gleichmäßig heißt: auch links und rechts zur Wandkante derselbe Abstand.",
    ],
    faq: [
      { q: "Wie verteile ich mehrere Bilder gleichmäßig?", a: "Ziehe die Gesamtbreite aller Bilder von der Wandbreite ab und verteile den Rest gleichmäßig auf alle Lücken, auch auf die beiden Randlücken. Der Rechner macht das automatisch." },
      { q: "Auf welcher Höhe hänge ich Bilder auf?", a: "Üblich ist eine Bildmitte auf etwa 145 bis 150 cm über dem Boden. Gib Bildhöhe und Aufhängung an, dann nennt der Rechner die genaue Bohrhöhe." },
      { q: "Was, wenn die Bilder unterschiedlich breit sind?", a: "Dieser Rechner geht von gleich breiten Bildern aus. Für gemischte Größen plane die Bilderwand einzeln. Diese Funktion kommt später." },
      { q: "Brauche ich eine Wasserwaage?", a: "Ja. Der Rechner liefert die Maße, die genaue waagerechte Ausrichtung prüfst du am besten mit einer Wasserwaage." },
    ],
    related: ["bild-mittig-aufhaengen", "regal-mittig-aufhaengen", "bilderwand-planen", "wandmitte-berechnen"],
  },

  "bilder-gleichmaessig-aufhaengen": {
    slug: "bilder-gleichmaessig-aufhaengen",
    group: GROUPS.bilder,
    calculator: "even",
    navLabel: "Bilder gleichmäßig verteilen",
    h1: "Bilder gleichmäßig aufhängen und Abstände berechnen",
    metaTitle: "Bilder gleichmäßig aufhängen: Abstände und Bohrpunkte berechnen",
    metaDescription:
      "Mehrere Bilder gleichmäßig an der Wand verteilen. Der Rechner ermittelt Abstände, Bohrpunkte und Bohrhöhe mit visueller Vorschau.",
    intro:
      "So verteilst du mehrere Bilder mit gleichem Abstand an der Wand. Gib die Maße ein, die Vorschau und die Bohrpunkte aktualisieren sich sofort.",
    forWhat: "Für eine Reihe gleich breiter Bilder, die gleichmäßig und symmetrisch hängen sollen.",
    whichValues: [
      "Wandbreite",
      "Anzahl der Bilder",
      "Bildbreite",
      "optional Bildhöhe und Bildmitte ab Boden für die Bohrhöhe",
    ],
    howItWorks:
      "Der freie Platz neben den Bildern wird gleichmäßig auf alle Lücken verteilt. So entsteht links, rechts und zwischen den Bildern derselbe Abstand.",
    example: {
      title: "Beispiel: 4 Bilder auf 240 cm Wand",
      lines: [
        "Wandbreite 240 cm, 4 Bilder, jedes 30 cm breit.",
        "Gesamtbreite: 4 × 30 = 120 cm. Freier Platz: 120 cm.",
        "5 Lücken: 120 ÷ 5 = 24 cm je Lücke.",
        "Bohrpunkte von links: 39, 93, 147, 201 cm.",
      ],
    },
    mistakes: [
      "Nur die Innenabstände gleich setzen und die Randabstände vergessen.",
      "Bildmitte und Bohrpunkt verwechseln.",
      "Die Aufhängung nicht in die Bohrhöhe einrechnen.",
    ],
    faq: [
      { q: "Was ist der Unterschied zwischen Randabstand und Innenabstand?", a: "Der Randabstand ist der Platz zur Wandkante, der Innenabstand der Platz zwischen zwei Bildern. Bei gleichmäßiger Verteilung sind beide gleich." },
      { q: "Kann ich den Außenabstand selbst festlegen?", a: "Ja. Trag im Rechner einen Außenabstand ein, dann wird nur der Innenraum gleichmäßig verteilt." },
    ],
    related: ["bilder-aufhaengen-abstand-berechnen", "bild-mittig-aufhaengen", "wandmitte-berechnen"],
  },

  "bild-mittig-aufhaengen": {
    slug: "bild-mittig-aufhaengen",
    group: GROUPS.bilder,
    calculator: "single",
    navLabel: "Einzelnes Bild auf Höhe",
    h1: "Einzelnes Bild auf gewünschter Höhe aufhängen",
    metaTitle: "Bild auf richtiger Höhe aufhängen: Bohrhöhe berechnen",
    metaDescription:
      "Berechne die genaue Bohrhöhe für ein einzelnes Bild. Gib Bildmitte ab Boden, Bildhöhe und Aufhängung ein, der Rechner zeigt, wo du bohren musst.",
    intro:
      "Du weißt, wo die Bildmitte sitzen soll, brauchst aber die genaue Bohrhöhe? Trag die drei Werte ein, der Rechner sagt dir, bei welcher Höhe du bohrst.",
    forWhat: "Für ein einzelnes Bild, das auf einer bestimmten Höhe hängen soll.",
    whichValues: ["Gewünschte Bildmitte ab Boden", "Bildhöhe", "Abstand der Aufhängung zur Oberkante"],
    howItWorks:
      "Aus Bildmitte und Bildhöhe ergibt sich die Oberkante. Davon wird der Abstand der Aufhängung zur Oberkante abgezogen. Das Ergebnis ist die Bohrhöhe.",
    example: {
      title: "Beispiel: Bildmitte 145 cm, Bildhöhe 60 cm, Aufhängung 5 cm",
      lines: [
        "Oberkante: 145 + 60 ÷ 2 = 175 cm.",
        "Bohrhöhe: 175 − 5 = 170 cm.",
        "Bohre bei 170 cm. Die Bildmitte liegt danach bei 145 cm.",
      ],
    },
    mistakes: [
      "Die Bohrhöhe auf die Bildmitte setzen. Gebohrt wird an der Aufhängung, nicht in der Bildmitte.",
      "Die Aufhängung ignorieren. Schnur oder Öse sitzen unter der Oberkante.",
      "Bildhöhe und Bildbreite vertauschen.",
    ],
    faq: [
      { q: "Auf welcher Höhe hängt man ein Bild richtig?", a: "Als Faustregel liegt die Bildmitte auf etwa 145 bis 150 cm, also ungefähr auf Augenhöhe. Über Möbeln orientierst du dich an der Möbelkante." },
      { q: "Wo muss ich bohren, damit die Bildmitte stimmt?", a: "An der Aufhängung, also über der Bildmitte. Der Rechner zieht den Abstand der Aufhängung zur Oberkante automatisch ab." },
    ],
    related: ["bilder-aufhaengen-abstand-berechnen", "regal-mittig-aufhaengen", "wandmitte-berechnen"],
  },

  "regal-mittig-aufhaengen": {
    slug: "regal-mittig-aufhaengen",
    group: GROUPS.regal,
    calculator: "shelf",
    navLabel: "Regal mittig montieren",
    h1: "Regal mittig montieren: Bohrlöcher berechnen",
    metaTitle: "Regal mittig aufhängen: Bohrlöcher und Abstand berechnen",
    metaDescription:
      "Berechne die Bohrlöcher, um ein Regal mittig oder an einer festen Position aufzuhängen. Mit Kontrollabstand und visueller Vorschau.",
    intro:
      "Gib Wandbreite, Regalbreite und Lochabstand ein. Der Rechner nennt dir die beiden Bohrlöcher von der linken Wandkante und den Kontrollabstand.",
    forWhat: "Für ein Regal mit zwei Befestigungspunkten, das mittig oder an einer festen Position sitzen soll.",
    whichValues: ["Wandbreite", "Regalbreite", "Lochabstand am Regal", "optional Höhe ab Boden", "Position: mittig oder eigener Abstand von links"],
    howItWorks:
      "Der Rechner bestimmt die Regalmitte und setzt die beiden Bohrlöcher symmetrisch dazu, sodass das Regal mittig sitzt. Der Kontrollabstand entspricht dem Lochabstand am Regal.",
    example: {
      title: "Beispiel: Wand 200 cm, Regal 80 cm, Lochabstand 60 cm",
      lines: [
        "Wandmitte und Regalmitte: 100 cm.",
        "Loch 1: 100 − 30 = 70 cm von links.",
        "Loch 2: 100 + 30 = 130 cm von links.",
        "Kontrollabstand: 60 cm, passt zum Lochabstand am Regal.",
      ],
    },
    mistakes: [
      "Wandmitte statt Regalmitte als Bezug nehmen, wenn das Regal nicht mittig sitzen soll.",
      "Den Lochabstand am Regal falsch messen. Miss von Lochmitte zu Lochmitte.",
      "Beide Löcher auf unterschiedlicher Höhe setzen. Prüfe die Waagerechte mit der Wasserwaage.",
    ],
    faq: [
      { q: "Wie hänge ich ein Regal gerade auf?", a: "Berechne beide Bohrlöcher, übertrage sie auf dieselbe Höhe und prüfe die Waagerechte mit einer Wasserwaage, bevor du bohrst." },
      { q: "Wie finde ich die Wandmitte?", a: "Die Wandmitte ist die halbe Wandbreite. Bei 200 cm Wand liegt sie bei 100 cm." },
      { q: "Was ist der Lochabstand?", a: "Der Abstand der beiden Befestigungslöcher am Regal, gemessen von Lochmitte zu Lochmitte." },
    ],
    related: ["bilder-aufhaengen-abstand-berechnen", "bild-mittig-aufhaengen", "wandmitte-berechnen", "bohrloch-abstand-berechnen"],
  },

  // Grundlagen-Seiten, die einen passenden MVP-Rechner mitnutzen.
  "wandmitte-berechnen": {
    slug: "wandmitte-berechnen",
    group: GROUPS.grundlagen,
    calculator: "shelf",
    navLabel: "Wandmitte berechnen",
    h1: "Wandmitte berechnen und Objekte mittig ausrichten",
    metaTitle: "Wandmitte berechnen: Objekte mittig aufhängen",
    metaDescription:
      "Finde die Wandmitte und richte Regale oder Objekte mittig aus. Der Rechner zeigt Wandmitte, Objektmitte und die beiden Bohrlöcher.",
    intro:
      "Die Wandmitte ist die halbe Wandbreite. Damit ein Objekt wirklich mittig sitzt, zählt aber die Objektmitte. Der Rechner zeigt beide und die passenden Bohrlöcher.",
    forWhat: "Wenn du ein Objekt mittig an der Wand ausrichten willst und die Bohrpunkte dazu brauchst.",
    whichValues: ["Wandbreite", "Objektbreite", "Lochabstand am Objekt"],
    howItWorks:
      "Die Wandmitte ist die halbe Wandbreite. Der Rechner legt die Objektmitte auf die Wandmitte und setzt die Bohrlöcher symmetrisch dazu.",
    example: {
      title: "Beispiel: Wand 180 cm",
      lines: [
        "Wandmitte: 180 ÷ 2 = 90 cm.",
        "Ein 60 cm breites Objekt beginnt bei 90 − 30 = 60 cm und endet bei 120 cm.",
        "Bei einem Lochabstand von 40 cm liegen die Löcher bei 70 cm und 110 cm.",
      ],
    },
    mistakes: [
      "Wandmitte mit Objektmitte gleichsetzen. Mittig sitzt das Objekt nur, wenn seine Mitte auf der Wandmitte liegt.",
      "Den Lochabstand nicht symmetrisch zur Mitte setzen.",
    ],
    faq: [
      { q: "Wie berechne ich die Wandmitte?", a: "Teile die Wandbreite durch zwei. Bei 180 cm liegt die Wandmitte bei 90 cm." },
      { q: "Reicht die Wandmitte zum mittigen Aufhängen?", a: "Nein. Du musst die Objektmitte auf die Wandmitte legen und die Bohrlöcher symmetrisch dazu setzen." },
    ],
    related: ["regal-mittig-aufhaengen", "bilder-aufhaengen-abstand-berechnen", "bild-mittig-aufhaengen"],
  },

  "bohrloch-abstand-berechnen": {
    slug: "bohrloch-abstand-berechnen",
    group: GROUPS.grundlagen,
    calculator: "shelf",
    navLabel: "Bohrloch-Abstand berechnen",
    h1: "Bohrloch-Abstand berechnen und übertragen",
    metaTitle: "Bohrloch-Abstand berechnen: Löcher sauber übertragen",
    metaDescription:
      "Berechne die Position von zwei Bohrlöchern und den Kontrollabstand, um sie sauber auf die Wand zu übertragen. Mit visueller Vorschau.",
    intro:
      "Zwei Bohrlöcher sauber auf die Wand bringen: Gib die Maße ein, der Rechner nennt beide Lochpositionen von links und den Kontrollabstand.",
    forWhat: "Für Objekte mit zwei Befestigungspunkten, deren Bohrlöcher du genau positionieren willst.",
    whichValues: ["Wandbreite", "Objektbreite", "Lochabstand am Objekt", "Position"],
    howItWorks:
      "Der Rechner setzt die beiden Löcher symmetrisch zur Objektmitte. Der Kontrollabstand entspricht dem Lochabstand und dient zur Überprüfung beim Anzeichnen.",
    example: {
      title: "Beispiel: Lochabstand 50 cm, Objekt mittig auf 200 cm Wand",
      lines: ["Objektmitte: 100 cm.", "Loch 1: 75 cm, Loch 2: 125 cm von links.", "Kontrollabstand: 50 cm."],
    },
    mistakes: [
      "Den Lochabstand von Kante zu Kante statt von Lochmitte zu Lochmitte messen.",
      "Die beiden Löcher auf unterschiedlicher Höhe anzeichnen.",
    ],
    faq: [
      { q: "Wie übertrage ich Bohrlöcher genau?", a: "Markiere beide Positionen von der Wandkante aus, prüfe den Kontrollabstand zwischen ihnen und kontrolliere die Waagerechte mit einer Wasserwaage." },
    ],
    related: ["regal-mittig-aufhaengen", "wandmitte-berechnen", "bohrpunkte-berechnen"],
  },

  "bohrpunkte-berechnen": {
    slug: "bohrpunkte-berechnen",
    group: GROUPS.grundlagen,
    calculator: "even",
    navLabel: "Bohrpunkte berechnen",
    h1: "Bohrpunkte berechnen für Bilder und Objekte",
    metaTitle: "Bohrpunkte berechnen: Bilder und Objekte sauber ausrichten",
    metaDescription:
      "Berechne Bohrpunkte und Abstände für Bilder und Wandobjekte. Mit visueller Vorschau, Bohrhöhe und Schritt-für-Schritt-Anleitung.",
    intro:
      "Berechne die Bohrpunkte für mehrere Bilder von der linken Wandkante aus. Die Vorschau zeigt Wand, Bilder, Bohrpunkte und Maße sofort.",
    forWhat: "Wenn du die genauen Bohrpunkte für eine Reihe von Bildern oder Objekten brauchst.",
    whichValues: ["Wandbreite", "Anzahl Objekte", "Objektbreite", "optional Höhe für die Bohrhöhe"],
    howItWorks:
      "Der Rechner verteilt die Objekte gleichmäßig und setzt den Bohrpunkt jeweils in die Objektmitte, gemessen von der linken Wandkante.",
    example: {
      title: "Beispiel: 3 Objekte auf 200 cm Wand",
      lines: ["Objektbreite 40 cm.", "Bohrpunkte von links: 40, 100, 160 cm."],
    },
    mistakes: [
      "Bohrpunkt und Objektkante verwechseln. Gebohrt wird meist in der Objektmitte.",
      "Von der falschen Seite messen. Bleib bei einer Bezugskante, hier links.",
    ],
    faq: [
      { q: "Was ist ein Bohrpunkt?", a: "Der Punkt, an dem du bohrst, damit das Objekt an der richtigen Stelle hängt. Bei einem Bild ist das meist die Bildmitte auf der Aufhängehöhe." },
    ],
    related: ["bilder-aufhaengen-abstand-berechnen", "bild-mittig-aufhaengen", "wandmitte-berechnen"],
  },

  "bilderwand-planen": {
    slug: "bilderwand-planen",
    group: GROUPS.bilder,
    calculator: "even",
    navLabel: "Bilderwand planen",
    h1: "Bilderwand planen: Abstände und Bohrpunkte",
    metaTitle: "Bilderwand planen: Abstände und Bohrpunkte berechnen",
    metaDescription:
      "Plane eine Bilderwand mit gleich großen Bildern. Der Rechner verteilt die Bilder gleichmäßig und nennt die Bohrpunkte. Mit Vorschau.",
    intro:
      "Für eine Bilderwand aus gleich großen Bildern verteilt der Rechner die Bilder gleichmäßig und nennt dir die Bohrpunkte. Für gemischte Größen kommt später eine eigene Funktion.",
    forWhat: "Für eine Bilderwand aus gleich breiten Bildern in einer Reihe.",
    whichValues: ["Wandbreite", "Anzahl Bilder", "Bildbreite", "optional Höhe"],
    howItWorks: "Die Bilder werden gleichmäßig über die Wandbreite verteilt, mit gleichem Abstand zwischen den Bildern und zu den Rändern.",
    example: {
      title: "Beispiel: 5 Bilder auf 250 cm Wand",
      lines: ["Bildbreite 30 cm. Gesamtbreite: 150 cm.", "Freier Platz 100 cm auf 6 Lücken: rund 16,7 cm je Lücke."],
    },
    mistakes: [
      "Unterschiedlich große Bilder mit demselben Rechner planen. Dafür kommt eine eigene Funktion.",
      "Den Randabstand vergessen.",
    ],
    faq: [
      { q: "Kann ich eine Bilderwand mit verschiedenen Größen planen?", a: "Dieser Rechner geht von gleich breiten Bildern aus. Eine Funktion für gemischte Größen ist geplant." },
    ],
    related: ["bilder-aufhaengen-abstand-berechnen", "bilder-gleichmaessig-aufhaengen", "bild-mittig-aufhaengen"],
  },

  "moebelgriffe-bohrabstand-berechnen": {
    slug: "moebelgriffe-bohrabstand-berechnen",
    group: GROUPS.moebel,
    calculator: "grip",
    navLabel: "Möbelgriffe bohren",
    h1: "Möbelgriffe bohren: Bohrabstand berechnen",
    metaTitle: "Möbelgriffe bohren: Bohrabstand und Lochabstand berechnen",
    metaDescription:
      "Berechne die Bohrlöcher für Möbelgriffe an Türen und Schubladen. Setze den Griff in der Vorschau, der Rechner zeigt Lochabstand und Abstände zu den Kanten.",
    intro:
      "Setze den Griff in der Vorschau auf die Front. Der Rechner nennt dir beide Bohrlöcher, den Lochabstand und die Abstände zu den Kanten. Der Griff rastet mittig ein.",
    forWhat:
      "Für Griffe und Knöpfe mit zwei Bohrlöchern an Schranktüren und Schubladen, horizontal oder vertikal montiert.",
    whichValues: [
      "Frontbreite der Tür oder Schublade",
      "Fronthöhe der Tür oder Schublade",
      "Griff-Lochabstand (oft auf dem Griff angegeben)",
      "Ausrichtung horizontal oder vertikal",
    ],
    howItWorks:
      "Der Rechner legt die Griffmitte fest und setzt die beiden Bohrlöcher symmetrisch dazu, mit dem halben Lochabstand nach links und rechts (oder oben und unten). So sitzt der Griff sauber und symmetrisch.",
    example: {
      title: "Beispiel: Schublade 45 cm breit, Lochabstand 12 cm, horizontal mittig",
      lines: [
        "Griffmitte mittig: 45 ÷ 2 = 22,5 cm von links.",
        "Bohrloch 1: 22,5 − 6 = 16,5 cm von links.",
        "Bohrloch 2: 22,5 + 6 = 28,5 cm von links.",
        "Beide Löcher auf derselben Höhe, der Lochabstand bleibt 12 cm.",
      ],
    },
    mistakes: [
      "Den Lochabstand am Griff falsch messen. Miss von Lochmitte zu Lochmitte.",
      "Beide Löcher auf unterschiedlicher Höhe setzen. Bei einem horizontalen Griff zählt eine gemeinsame Höhe.",
      "Den Griff nicht mittig setzen, obwohl er mittig wirken soll. Die Griffmitte gehört auf die Frontmitte.",
    ],
    faq: [
      { q: "Wie finde ich den Lochabstand eines Griffs?", a: "Das ist der Abstand der beiden Schraublöcher, gemessen von Lochmitte zu Lochmitte. Häufig steht er als Bohrabstand auf der Verpackung." },
      { q: "Wo sitzt ein Griff an einer Schublade üblicherweise?", a: "Bei Schubladen meist horizontal mittig. Bei Türen oft vertikal an der Öffnungsseite, einige Zentimeter von der Kante. Den genauen Abstand legst du im Rechner fest." },
      { q: "Horizontal oder vertikal montieren?", a: "Schubladen bekommen meist horizontale Griffe, hohe Türen oft vertikale. Stelle die Ausrichtung im Rechner um, dann passt die Vorschau." },
    ],
    related: ["bohrloch-abstand-berechnen", "regal-mittig-aufhaengen", "gardinenstange-abstand-berechnen", "wandmitte-berechnen"],
  },

  "gardinenstange-abstand-berechnen": {
    slug: "gardinenstange-abstand-berechnen",
    group: GROUPS.moebel,
    calculator: "curtain",
    navLabel: "Gardinenstange ausrichten",
    h1: "Gardinenstange ausrichten: Halterabstand und Länge berechnen",
    metaTitle: "Gardinenstange ausrichten: Halterabstand und Länge berechnen",
    metaDescription:
      "Berechne Halterpositionen, Überstand und Stangenlänge für deine Gardinenstange. Mit interaktiver Vorschau von Fenster, Stange und Haltern.",
    intro:
      "Trag Fensterbreite, Überstand und Halterabstand ein. Der Rechner nennt dir die nötige Stangenlänge, die Halterpositionen und die empfohlene Höhe über dem Fenster.",
    forWhat:
      "Für eine Gardinenstange über einem Fenster, die du symmetrisch ausrichten und sauber befestigen willst.",
    whichValues: [
      "Fensterbreite",
      "Fensterhöhe (für die Skizze)",
      "Überstand je Seite",
      "Halterabstand von der Fensterkante",
      "Höhe über dem Fenster",
      "optional die Wandbreite für Halterpositionen ab Wandkante",
    ],
    howItWorks:
      "Die Stangenlänge ist die Fensterbreite plus zweimal Überstand. Die Halter sitzen um den Halterabstand nach außen versetzt zu den Fensterkanten, symmetrisch auf beiden Seiten. Mit der Wandbreite rechnet der Rechner die Halterpositionen ab der linken Wandkante aus.",
    example: {
      title: "Beispiel: Fenster 120 cm, Überstand 15 cm je Seite, Halter 8 cm von der Kante",
      lines: [
        "Stangenlänge: 120 + 2 × 15 = 150 cm.",
        "Halter je 8 cm außerhalb der Fensterkanten.",
        "Bei 12 cm über dem Fenster montieren und mit der Wasserwaage ausrichten.",
      ],
    },
    mistakes: [
      "Die Stange zu kurz wählen. Plane den Überstand mit ein, damit der Vorhang seitlich nicht ins Fenster ragt.",
      "Die Halter zu weit außen setzen, sodass sie außerhalb der Stange liegen. Der Halterabstand sollte kleiner als der Überstand sein.",
      "Die Höhe über dem Fenster vergessen. Etwas Abstand wirkt ruhiger und lässt mehr Licht herein.",
    ],
    faq: [
      { q: "Wie lang muss eine Gardinenstange sein?", a: "Mindestens so breit wie das Fenster plus der gewünschte Überstand auf beiden Seiten. Bei 120 cm Fenster und 15 cm Überstand je Seite sind das 150 cm." },
      { q: "Wie weit über dem Fenster montiere ich die Stange?", a: "Üblich sind etwa 10 bis 15 cm über der Fensteroberkante. Mehr Abstand wirkt ruhiger und lässt mehr Tageslicht herein." },
      { q: "Wie viel Überstand braucht eine Gardinenstange?", a: "Oft 10 bis 20 cm je Seite, damit der geöffnete Vorhang neben dem Fenster Platz hat und das Fenster frei bleibt." },
    ],
    related: ["bohrloch-abstand-berechnen", "moebelgriffe-bohrabstand-berechnen", "wandmitte-berechnen", "regal-mittig-aufhaengen"],
  },
};

export const PAGE_LIST = Object.values(PAGES);

export function getPage(slug) {
  return PAGES[slug] || null;
}
